import React from 'react'
import { useSelector } from 'react-redux';
import {
    Redirect,
    Route,
    Switch
} from "react-router-dom";

import SpinnerLoader from '../components/Layout/Loaders/SpinnerLoader'
import { loadable } from '../components/Layout/Common/Loadable'
import { parseTheme } from '../utils/common'

import SiteManagement from '../components/Admin/SiteManagement'
// import Onboarding from '../components/Onboarding/OnboardingOld'
import Onboarding from '../components/Onboarding/Onboarding'
import Overview from '../components/Overview/Overview'
import CheckoutExpress from '../components/Help/CheckoutExpress'

import Audits from '../components/Reports/Audits/Audits'
import Audit from '../components/Reports/Audits/Audit'
import NoMatch from './NoMatch';
// import ErrorPage from '../components/Layout/Common/ErrorPage';

const loadableOptions = {
    fallback: <SpinnerLoader />
}

const SitePerformance = loadable(() => import('../components/Performance/SitePerformance'), loadableOptions)
const PageSpeedReport = loadable(() => import('../components/Performance/PageSpeedReport'), loadableOptions)
const SeoPerformance = loadable(() => import('../components/Performance/SeoPerformance'), loadableOptions)
const SocialMediaPerformance = loadable(() => import('../components/Performance/SocialMediaPerformance'), loadableOptions)
const EmailPerformance = loadable(() => import('../components/Performance/EmailPerformance'), loadableOptions)

const QuickStats = loadable(() => import('../components/Onboarding/QuickStats'), loadableOptions)
const Recommendations = loadable(() => import('../components/Reports/Recommendations/Recommendations'), loadableOptions)
const Ecommerce = loadable(() => import('../components/Campaign/Ecommerce'), loadableOptions)
const PaidAdSummary = loadable(() => import('../components/Campaign/PaidAdSummary'), loadableOptions)
const ChannelPerformance = loadable(() => import('../components/Performance/ChannelPerformance'), loadableOptions)
const OtherAds = loadable(() => import('../components/Campaign/OtherAds'), loadableOptions)

const GoogleBusinessProfile = loadable(() => import('../components/GoogleBusinessProfile/GoogleBusinessProfile'), loadableOptions)

const GoalsAndEvents = loadable(() => import('../components/Goals/GoalsAndEvents'), loadableOptions)

const MonthlyReport = loadable(() => import('../components/Reports/MonthlyReport'), loadableOptions)
const AnnualReport = loadable(() => import('../components/Reports/AnnualReport'), loadableOptions)
const ScoreCardReport = loadable(() => import('../components/Reports/ScoreCardReport'), loadableOptions)

const BrandSummary = loadable(() => import('../components/Reports/BrandSummary'), loadableOptions)
const OrganizationSummary = loadable(() => import('../components/Reports/OrganizationSummary'), loadableOptions)

const UnitSalesPerformance = loadable(() => import('../components/Reports/UnitSalesReport'), loadableOptions)
const BillingManagement = loadable(() => import('../components/Stripe/BillingManagement'), loadableOptions)
const Organizations = loadable(() => import('../components/Admin/Organizations'), loadableOptions)
const SiteUpload = loadable(() => import('../components/Admin/SiteUpload'), loadableOptions)

const UserSettings  = loadable(() => import('../components/Auth/UserSettings'), loadableOptions)
const UnitSalesEntry  = loadable(() => import('../components/Admin/UnitSalesEntry'), loadableOptions)
const RevenueEntry  = loadable(() => import('../components/Admin/RevenueEntry'), loadableOptions)

const TrialExpired = loadable(() => import('../components/Help/TrialExpired'), loadableOptions)
const HelpCenter = loadable(() => import('../components/Help/HelpCenter'), loadableOptions)
const NotificationCenter = loadable(() => import('../components/Admin/NotificationCenter'), loadableOptions)
const FrontEndLogs = loadable(() => import('../components/Admin/FrontEndLogs'), loadableOptions)
const GoPro = loadable(() => import('../components/Help/GoPro'), loadableOptions)

const TaskManagment = loadable(() => import('../components/Admin/TaskManagement'), loadableOptions)
const EmailActivity = loadable(() => import('../components/Admin/EmailActivity'), loadableOptions)

const Directories = loadable(() => import('../components/Help/Directories'), loadableOptions)

const UsersSection = loadable(() => import('../components/Admin/UsersSection'), loadableOptions)

const CRMConnectReport = loadable(() => import('../components/Reports/CRMConnectReport'), loadableOptions) 

const PreboardingCheckList = loadable(() => import('../components/Onboarding/PreboardingCheckList'), loadableOptions) 


function AuthRoutes(){
    
    const isAdmin = useSelector(state => state.session.isAdmin)
    const trialMode = useSelector(state => state.session.trialMode)
    const model = useSelector(state => state.site.model)
    const theme = parseTheme()

    return <Switch>
        <Route exact path="/">
            <Redirect to="/overview" />
        </Route>
        <Route exact path="/login">
            <Redirect to="/overview" />
        </Route>

        <Route exact path="/overview">
            <Overview />
        </Route>

        <Route exact path="/quick-stats">
            <QuickStats />
        </Route>

        <Route exact path="/insights">
            {isAdmin ? <Recommendations /> : <Redirect to="/audits" />}
        </Route>

        <Route exact path="/site-performance">
            <SitePerformance />
        </Route>

        <Route exact path="/pagespeed/:deviceType" component={PageSpeedReport} />
        <Route exact path="/pagespeed">
            <PageSpeedReport />
        </Route>

        <Route exact path="/seo">
            <SeoPerformance />
        </Route>
        <Route exact path="/social-media">
            <SocialMediaPerformance />
        </Route>

        <Route exact path="/ads-ecommerce">
            <Ecommerce />
        </Route>

        <Route exact path="/ads-paid">
            <PaidAdSummary />
        </Route>
        
        <Route exact path="/channel-performance/:channel" component={ChannelPerformance} />
        <Route exact path="/channel-performance">
            <ChannelPerformance />
        </Route>

        <Route exact path="/email-performance">
            <EmailPerformance />
        </Route>

        <Route exact path="/ads-other">
            <OtherAds />
        </Route>

        <Route exact path="/google-business-profile">
            <GoogleBusinessProfile />
        </Route>

        <Route exact path="/goals">
            <GoalsAndEvents />
        </Route>

        <Route exact path="/report-monthly">
            <MonthlyReport />
        </Route>
        <Route exact path="/report-annual">
            <AnnualReport />
        </Route>
        <Route exact path="/audits">
            <Audits />
        </Route>
        <Route exact path="/audit/:auditId">
            <Audit />
        </Route>
        <Route exact path="/unit-sales">
            <UnitSalesPerformance />
        </Route>
        <Route exact path="/score-card">
            <ScoreCardReport />
        </Route>
        <Route exact path="/summaries-brand">
            <BrandSummary />
        </Route>
        <Route exact path="/summaries-company">
            <BrandSummary />
        </Route>
        <Route exact path="/summaries-group">
            <OrganizationSummary />
        </Route>

        <Route exact path="/billing-management">
            {(isAdmin || model.reseller) ? <BillingManagement /> : <Redirect to="/overview" />}
        </Route>

        <Route exact path="/admin-organizations">
            <Organizations />
        </Route>

        <Route exact path="/logs">
            {isAdmin ? <FrontEndLogs /> : <Redirect to="/overview" />}
        </Route>

        <Route exact path="/admin">
            <SiteManagement />
        </Route>        
        <Route exact path="/admin/:siteId/:type" component={SiteManagement} />

        <Route exact path="/onboarding/:siteId/:type" component={Onboarding} />
        <Route exact path="/onboarding">
            <Onboarding />
        </Route>

        <Route exact path="/admin-site-upload">
            {!(model.userSite?.type === "ORG" ||
            !model.siteFixed || 
            !model.regFixed) ? <Redirect to="/overview" /> : <SiteUpload />}
        </Route>

        <Route exact path="/settings">
            <UserSettings />
        </Route>
        <Route exact path="/settings/:id" component={UserSettings} />

        <Route exact path="/unit-sales-entry">
            <UnitSalesEntry />
        </Route>       
        <Route exact path="/revenue-entry">
            {(!isAdmin && (trialMode || model?.userSite?.attr?.isFranchise || (model.reseller && model.isAffiliate))) ? <Redirect to="/overview" /> : <RevenueEntry /> } 
        </Route>

        <Route exact path="/trial-expired">
            <TrialExpired />
        </Route>
        <Route exact path="/help">
            <HelpCenter />
        </Route>
        <Route exact path="/directories">
            {(!isAdmin && 
				(!model?.siteFixed || model?.salesTokens?.length === 0 || !model?.salesTokens[0].attr?.directoryAccess)) ? <Redirect to="/overview" /> : <Directories /> }
        </Route>

        <Route exact path="/checkout/:sessionId" component={CheckoutExpress} />

        <Route exact path="/upgrade">
            <GoPro />
        </Route>

        <Route exact path="/notifications">
            <NotificationCenter />
        </Route>

        <Route exact path="/task-manager">
            {trialMode ? <Redirect to="/overview" /> : <TaskManagment /> }
        </Route>

        <Route exact path="/email-activity">
            {!isAdmin ? <Redirect to="/overview" /> : <EmailActivity />}
        </Route>

        {/* <Route exact path="/error">
            {isAdmin ? <ErrorPage /> : <Redirect to="/overview" />}
        </Route> */}
        
        <Route exact path="/users">
            {!isAdmin ? <Redirect to="/admin" /> : <UsersSection />}
        </Route>

        <Route exact path="/crm-connect">
            <CRMConnectReport />
        </Route>

        <Route exact path="/initial-scan">
            <PreboardingCheckList />
        </Route>

        <Route path="*">
            <NoMatch redirect="/overview" />
        </Route>
    </Switch>
}

export default AuthRoutes